// AI rules section (US-112) — question guide, banned words, and sales rules
// (with AI assist). JSX lifted verbatim from BusinessEditor.

import { useT } from '@barel/i18n';
import { BAiButton, Card, FieldAssembly } from '@barel/ui';

import { BulletListEditor } from '../PainEditor';
import { SentenceListEditor } from '../ValuePropEditor';
import type { BusinessDraft } from '../lib/defaults';
import { SectionHeader } from './SectionHeader';
import type { RunAi, SetField } from './types';

export interface AiRulesSectionProps {
  questionGuide: BusinessDraft['questionGuide'];
  bannedWords: BusinessDraft['bannedWords'];
  salesRules: BusinessDraft['salesRules'];
  onChange: SetField;
  runAi: RunAi;
  aiBusyField: string | null;
}

export function AiRulesSection({
  questionGuide,
  bannedWords,
  salesRules,
  onChange,
  runAi,
  aiBusyField,
}: AiRulesSectionProps) {
  const t = useT('businesses');

  return (
    <Card padding="lg" radius="2xl" data-testid="business-section-ai-rules">
      <SectionHeader title={t('sections.aiRules')} subtitle={t('aiRules.subtitle')} />
      <div className="flex flex-col gap-4">
        <FieldAssembly
          label={t('aiRules.questionsLabel')}
          hint={t('aiRules.questionsHint')}
          action={
            <BAiButton
              onClick={() => void runAi('questionGuide')}
              busy={aiBusyField === 'questionGuide'}
              label={t('aiRules.ai')}
              busyLabel={t('aiRules.aiBusy')}
              data-testid="business-questions-ai"
            />
          }
        >
          <SentenceListEditor
            values={questionGuide}
            onChange={(next) => onChange('questionGuide', next)}
            placeholder={t('aiRules.questionsPlaceholder')}
            addLabel={t('aiRules.questionsAdd')}
            testId="business-questions"
          />
        </FieldAssembly>
        <FieldAssembly label={t('aiRules.bannedLabel')} hint={t('aiRules.bannedHint')}>
          <BulletListEditor
            values={bannedWords}
            onChange={(next) => onChange('bannedWords', next)}
            placeholder={t('aiRules.bannedPlaceholder')}
            addLabel={t('aiRules.bannedAdd')}
            tone="danger"
            testId="business-banned"
          />
        </FieldAssembly>
        <FieldAssembly
          label={t('aiRules.salesLabel')}
          hint={t('aiRules.salesHint')}
          action={
            <BAiButton
              onClick={() => void runAi('salesRules')}
              busy={aiBusyField === 'salesRules'}
              label={t('aiRules.ai')}
              busyLabel={t('aiRules.aiBusy')}
              data-testid="business-sales-ai"
            />
          }
        >
          <SentenceListEditor
            values={salesRules}
            onChange={(next) => onChange('salesRules', next)}
            placeholder={t('aiRules.salesPlaceholder')}
            addLabel={t('aiRules.salesAdd')}
            testId="business-sales"
          />
        </FieldAssembly>
      </div>
    </Card>
  );
}
